/**
 * ForexPanel — Phase 11 forex paper account view rendered inside the
 * Simulation Lab when the OANDA paper account is selected.
 *
 * INVARIANTS (mirrored from the Phase 1-10 forex backend):
 *   - LIVE_TRADING_BLOCKED / LIVE_LIMITED_BLOCKED / LIVE_FULL_BLOCKED
 *     banners are ALWAYS rendered. There is no UI control here that can
 *     flip them. There is no live-trading activation button. There is no
 *     order form.
 *   - This panel is read-only. It does not import any service that
 *     submits, cancels, or closes broker orders.
 *   - The OANDA API token is never displayed. We render only an
 *     "Configured / Unconfigured" indicator based on `oanda_configured`.
 */
import React from 'react';
import { Lock, Shield, AlertTriangle, CheckCircle2, Activity, Ban } from 'lucide-react';

import { ForexBundle } from '../services/forexService';
import { RegimeStateWidget } from './RegimeStateWidget';
import { SignalHistoryTable } from './SignalHistoryTable';
import { DailyPnLChart } from './DailyPnLChart';

export interface ForexPanelProps {
  bundle: ForexBundle | null;
  loading: boolean;
  error: string | null;
}

function formatNumber(n: number, decimals: number = 2): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function StatusBadge({
  label,
  tone,
  icon: Icon,
}: {
  label: string;
  tone: 'blocked' | 'paper' | 'ok' | 'warn';
  icon: React.ComponentType<{ size?: number }>;
}) {
  const palette: Record<string, string> = {
    blocked: 'bg-rose-500/10 text-rose-600 border-rose-500/30',
    paper: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    ok: 'bg-sky-500/10 text-sky-600 border-sky-500/30',
    warn: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border ${palette[tone]}`}
      data-testid={`forex-status-${tone}`}
    >
      <Icon size={10} />
      {label}
    </span>
  );
}

export default function ForexPanel({ bundle, loading, error }: ForexPanelProps) {
  if (loading && !bundle) {
    return (
      <div className="p-6 rounded-3xl border border-[var(--card-border)] bg-[var(--card-bg)]" data-testid="forex-panel-loading">
        <div className="text-xs uppercase tracking-widest text-[var(--muted-text)]">
          Loading forex paper account…
        </div>
      </div>
    );
  }
  if (error && !bundle) {
    return (
      <div className="p-6 rounded-3xl border border-rose-500/30 bg-rose-500/5" data-testid="forex-panel-error">
        <div className="flex items-center gap-2 text-rose-600 font-black text-sm">
          <AlertTriangle size={16} /> Forex bridge unreachable
        </div>
        <div className="text-xs text-[var(--muted-text)] mt-1">{error}</div>
      </div>
    );
  }
  if (!bundle) return null;

  const { account, positions, prices, health, systemState, paperSession, liveReadiness } = bundle;

  return (
    <div className="space-y-6" data-testid="forex-panel">
      {/* SAFETY BANNER — always visible. There is no control here that can
          unlock live trading. */}
      <div
        className="p-5 rounded-3xl border-2 border-rose-500/40 bg-rose-500/5"
        data-testid="forex-live-blocked-banner"
      >
        <div className="flex items-center gap-3 mb-3">
          <Ban size={20} className="text-rose-600" />
          <div className="text-sm font-black text-rose-600 uppercase tracking-widest">
            OANDA Live Trading: BLOCKED
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
          <div className="flex items-center gap-2">
            <Lock size={12} className="text-rose-600" />
            <span className="font-black uppercase tracking-wider text-rose-600">LIVE_LIMITED:</span>
            <span className="font-black text-rose-600">{account.live_limited_status}</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock size={12} className="text-rose-600" />
            <span className="font-black uppercase tracking-wider text-rose-600">LIVE_FULL:</span>
            <span className="font-black text-rose-600">{account.live_full_status}</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield size={12} className="text-emerald-600" />
            <span className="font-black uppercase tracking-wider text-emerald-600">Mode:</span>
            <span className="font-black text-emerald-600">{systemState.current_state}</span>
          </div>
        </div>
        <div className="mt-3 text-[10px] text-[var(--muted-text)] italic">
          Phase 11 read-only bridge. No order entry, no live broker writes.
          OANDA practice URL only. OANDA live writes disabled.
        </div>
      </div>

      {/* PAPER VALIDATION STATUS BANNER */}
      <div
        className={
          'p-4 rounded-3xl border ' +
          (paperSession.validation_complete
            ? 'border-emerald-500/30 bg-emerald-500/5'
            : 'border-amber-500/30 bg-amber-500/5')
        }
        data-testid="forex-validation-banner"
      >
        <div className="flex items-center gap-2 mb-1">
          {paperSession.validation_complete ? (
            <CheckCircle2 size={14} className="text-emerald-600" />
          ) : (
            <AlertTriangle size={14} className="text-amber-600" />
          )}
          <span
            className={
              'text-xs font-black uppercase tracking-widest ' +
              (paperSession.validation_complete ? 'text-emerald-600' : 'text-amber-600')
            }
          >
            Paper Validation: {paperSession.validation_status_label}
          </span>
        </div>
        <div className="text-[11px] text-[var(--muted-text)]">
          {paperSession.required_days}-day paper window —
          {' '}elapsed{' '}{paperSession.elapsed_days.toFixed(1)} days. LIVE_LIMITED review:{' '}
          <span className="font-black text-rose-600">{liveReadiness.live_limited_decision}</span>{' '}
          · approvals {liveReadiness.approval_count}/{liveReadiness.required_approvals}.
        </div>
      </div>

      {/* ACCOUNT SUMMARY */}
      <div className="p-5 rounded-3xl border border-[var(--card-border)] bg-[var(--card-bg)]">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <StatusBadge label="PAPER TRADING ONLY" tone="paper" icon={Shield} />
          <StatusBadge label={account.broker} tone="ok" icon={Activity} />
          <StatusBadge
            label={account.oanda_configured ? 'OANDA Configured' : 'OANDA Unconfigured'}
            tone={account.oanda_configured ? 'ok' : 'warn'}
            icon={account.oanda_configured ? CheckCircle2 : AlertTriangle}
          />
          <StatusBadge label={`Practice ${account.environment}`} tone="ok" icon={Lock} />
          <StatusBadge label="OANDA Live Writes Disabled" tone="blocked" icon={Ban} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Account ID" value={account.account_id} mono />
          <Stat label="Account Type" value={account.account_type} />
          <Stat label="System State" value={account.system_state} />
          <Stat label="Equity" value={`${account.currency} ${formatNumber(account.equity)}`} />
          <Stat label="Balance" value={`${account.currency} ${formatNumber(account.balance)}`} />
          <Stat
            label="Margin Available"
            value={`${account.currency} ${formatNumber(account.margin_available)}`}
          />
          <Stat label="Margin Used" value={`${account.currency} ${formatNumber(account.margin_used)}`} />
          <Stat label="Leverage" value={`${account.leverage.toFixed(0)}x`} />
        </div>
      </div>

      {/* CURRENCY PAIRS */}
      <div className="p-5 rounded-3xl border border-[var(--card-border)] bg-[var(--card-bg)]" data-testid="forex-prices">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-black uppercase tracking-widest text-[var(--app-text)]">
            Currency Pairs (Practice)
          </div>
          <div className="text-[10px] text-[var(--muted-text)]">
            spread shown in pips
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {prices.map((q) => (
            <div
              key={q.symbol}
              className="p-3 rounded-2xl border border-[var(--card-border)] bg-[var(--input-bg)]/30"
              data-testid={`forex-quote-${q.symbol}`}
            >
              <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">
                {q.symbol}
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="text-lg font-black text-[var(--app-text)]">{q.bid.toFixed(5)}</div>
                <div className="text-[10px] text-[var(--muted-text)]">/{q.ask.toFixed(5)}</div>
              </div>
              <div className="text-[10px] font-bold text-emerald-600 mt-1">
                {q.spread_pips.toFixed(1)} pips
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* OPEN POSITIONS */}
      <div className="p-5 rounded-3xl border border-[var(--card-border)] bg-[var(--card-bg)]" data-testid="forex-positions">
        <div className="flex items-center gap-3 mb-3">
          <div className="text-xs font-black uppercase tracking-widest text-[var(--app-text)]">
            Open Forex Positions
          </div>
          <div className="text-[10px] text-[var(--muted-text)]">
            count: {account.open_positions_count}
          </div>
        </div>
        {positions.length === 0 ? (
          <div className="text-[11px] text-[var(--muted-text)] italic">
            No open positions. (Phase 11 bridge surfaces an empty positions list — Phase 8B paper
            execution must be running and reachable for live values.)
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {positions.map((p) => (
              <div
                key={`${p.symbol}-${p.side}`}
                className="p-3 rounded-2xl border border-[var(--card-border)] bg-[var(--input-bg)]/30"
                data-testid={`forex-position-${p.symbol}-${p.side}`}
              >
                <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">
                  {p.symbol} · {p.side.toUpperCase()}
                </div>
                <div className="text-sm font-black text-[var(--app-text)] mt-1">
                  {formatNumber(p.units, 0)} units
                </div>
                <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
                  entry {p.entry_price.toFixed(5)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DASHBOARD WIDGETS — Regime State, Signals, Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <RegimeStateWidget />
        <div className="lg:col-span-2">
          <DailyPnLChart />
        </div>
      </div>

      <SignalHistoryTable />

      {/* HEALTH (read-only) */}
      <div className="p-5 rounded-3xl border border-[var(--card-border)] bg-[var(--card-bg)]" data-testid="forex-health">
        <div className="text-xs font-black uppercase tracking-widest text-[var(--app-text)] mb-3">
          Broker Health (Read-Only)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="State" value={health.state} />
          <Stat label="Reachable" value={health.api_reachable ? 'yes' : 'no'} />
          <Stat label="Latency" value={`${health.latency_ms.toFixed(0)} ms`} />
          <Stat label="Errors" value={`${health.error_rate_pct.toFixed(1)}%`} />
        </div>
        <div className="text-[10px] text-[var(--muted-text)] italic mt-3">
          Practice URL: <code>{health.practice_url}</code>. Live URL is never reachable from this
          bridge.
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest">
        {label}
      </div>
      <div
        className={
          'text-sm font-black text-[var(--app-text)] mt-0.5 ' +
          (mono ? 'font-mono text-[12px]' : '')
        }
      >
        {value}
      </div>
    </div>
  );
}
